import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the StateCodeMasterContext
const StateCodeMasterContext = createContext();

// Custom hook to use the StateCodeMasterContext
export const useStateCode = () => useContext(StateCodeMasterContext);

// Provider component to wrap the app
export const StateCodeMasterProvider = ({ children }) => {
  const [stateCodes, setStateCodes] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/state-codes"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStateCodes = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual departments master
      const { data, current_page, last_page, per_page, total } = response.data;

      setStateCodes(data.data || []); 

      setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching state codes master:", error);
      setError(error.response?.data?.message || "Failed to fetch state codes.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch state codes master from the API on load
  useEffect(() => {
    fetchStateCodes();
  }, [API_URL, token]);

  // Add a new department
  const addStateCode = async (newStateCode) => {
    try {
      const response = await axios.post(API_URL, newStateCode, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setStateCodes((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding state codes:", error);
    }
  };

  // Update an existing department
  const updateStateCode = async (updatedStateCode) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedStateCode.id}`, updatedStateCode, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setStateCodes((prev) =>
        prev.map((stateCode) =>
          stateCode.id === updatedStateCode.id ? { ...stateCode, ...response.data?.data } : stateCode
        )
      );
    } catch (error) {
      console.error("Error updating state code master:", error);
    }
  };

  // Delete a state code
  const deleteStateCode = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setStateCodes((prev) => prev.filter((stateCode) => stateCode.id !== id));
    } catch (error) {
      console.error("Error deleting stateCode:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      stateCodes,
      addStateCode,
      updateStateCode,
      deleteStateCode,
    }),
    [stateCodes, isLoading] // Recalculate the value only when `stateCode` changes
  );

  return <StateCodeMasterContext.Provider value={value}>{children}</StateCodeMasterContext.Provider>;
};
